import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const ChatContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [ws, setWs] = useState(null);
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [teams, setTeams] = useState([]);
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [activeTeam, setActiveTeam] = useState(null);
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const reconnectTimeout = useRef(null);
  const typingTimers = useRef({});

  // --- Call signaling state ---
  // incomingCall: set when a call_offer arrives from another user
  const [incomingCall, setIncomingCall] = useState(null);
  // callSignals: queue of signaling events for multi-party support
  const [callSignals, setCallSignals] = useState([]);
  // Legacy single signal for backward compatibility
  const [callSignal, setCallSignal] = useState(null);

  // WebSocket connection
  const connectWS = useCallback(() => {
    if (!user || !token) return;
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws?token=${token}`;
    
    const socket = new WebSocket(wsUrl);
    
    socket.onopen = () => {
      setConnected(true);
      wsRef.current = socket;
      setWs(socket);
    };
    
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWSEvent(data);
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };
    
    socket.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Reconnect after 3s
      reconnectTimeout.current = setTimeout(connectWS, 3000);
    };
    
    socket.onerror = (err) => {
      console.error('WS error:', err);
    };
    
    return socket;
  }, [user, token]);

  const handleWSEvent = useCallback((data) => {
    switch (data.type) {
      case 'welcome':
        if (data.online_users) {
          const onlineMap = {};
          data.online_users.forEach(u => { onlineMap[u.user_id] = true; });
          setOnlineUsers(onlineMap);
        }
        break;
      
      case 'presence':
        setOnlineUsers(prev => ({
          ...prev,
          [data.user_id]: data.status === 'online'
        }));
        break;
      
      case 'message_new':
        setMessages(prev => {
          if (prev.some(m => m.message_id === data.message_id)) return prev;
          return [...prev, data];
        });
        // Increment unread if not in active channel
        setUnreadCounts(prev => ({
          ...prev,
          [data.channel_id]: (prev[data.channel_id] || 0) + 1
        }));
        break;
      
      case 'message_ack':
        break;
      
      case 'typing':
        if (data.is_typing) {
          setTypingUsers(prev => ({
            ...prev,
            [data.room_id]: { ...prev[data.room_id], [data.user_id]: data.username }
          }));
          // Auto-clear typing after 5 seconds
          const key = `${data.room_id}_${data.user_id}`;
          if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
          typingTimers.current[key] = setTimeout(() => {
            setTypingUsers(prev => {
              const roomTyping = { ...prev[data.room_id] };
              delete roomTyping[data.user_id];
              return { ...prev, [data.room_id]: roomTyping };
            });
          }, 5000);
        } else {
          setTypingUsers(prev => {
            const roomTyping = { ...prev[data.room_id] };
            delete roomTyping[data.user_id];
            return { ...prev, [data.room_id]: roomTyping };
          });
        }
        break;
      
      case 'receipt_read':
        setMessages(prev => prev.map(m => 
          m.message_id === data.message_id
            ? { ...m, read_by: [...(m.read_by || []), data.user_id] }
            : m
        ));
        break;
      
      case 'room_joined':
      case 'room_left':
        break;

      // ---- WebRTC Call Signaling Events ----
      case 'call_offer':
        // Incoming call from another user
        console.log('[ChatContext] Received call_offer from:', data.from_user_id, data.from_username);
        if (data.is_mesh) {
          // Mid-call mesh offer from new participant joining group call
          const meshSig = {
            type: 'call_offer',
            from_user_id: data.from_user_id,
            from_username: data.from_username,
            sdp: data.sdp,
            sdp_type: data.sdp_type,
            is_video: data.is_video,
            is_mesh: true,
            timestamp: Date.now()
          };
          setCallSignals(prev => [...prev, meshSig]);
        } else {
          setIncomingCall({
            from_user_id: data.from_user_id,
            from_username: data.from_username,
            sdp: data.sdp,
            sdp_type: data.sdp_type,
            is_video: data.is_video,
            caller_name: data.caller_name || data.from_username,
            timestamp: Date.now()
          });
        }
        break;

      case 'call_answer':
        // Remote user accepted our call
        console.log('[ChatContext] Received call_answer from:', data.from_user_id);
        {
          const answerSig = {
            type: 'call_answer',
            from_user_id: data.from_user_id,
            sdp: data.sdp,
            sdp_type: data.sdp_type,
            timestamp: Date.now()
          };
          setCallSignal(answerSig);
          setCallSignals(prev => [...prev, answerSig]);
        }
        break;

      case 'ice_candidate':
        // ICE candidate from remote peer
        console.log('[ChatContext] Received ice_candidate from:', data.from_user_id);
        {
          const iceSig = {
            type: 'ice_candidate',
            from_user_id: data.from_user_id,
            candidate: data.candidate,
            timestamp: Date.now()
          };
          setCallSignal(iceSig);
          setCallSignals(prev => [...prev, iceSig]);
        }
        break;

      case 'call_end':
        // Remote user ended the call
        console.log('[ChatContext] Received call_end from:', data.from_user_id);
        {
          const endSig = {
            type: 'call_end',
            from_user_id: data.from_user_id,
            timestamp: Date.now()
          };
          setCallSignal(endSig);
          setCallSignals(prev => [...prev, endSig]);
        }
        break;

      // ---- Pin Updates ----
      case 'message_pinned':
        setMessages(prev => prev.map(m =>
          m.message_id === data.message_id
            ? { ...m, pinned: true, pinned_by: data.user_id }
            : m
        ));
        break;

      case 'message_unpinned':
        setMessages(prev => prev.map(m =>
          m.message_id === data.message_id
            ? { ...m, pinned: false, pinned_by: null }
            : m
        ));
        break;

      case 'message_deleted':
        setMessages(prev => prev.map(m =>
          m.message_id === data.message_id
            ? { ...m, deleted: true, content: 'This message was deleted', attachments: [], code_snippet: null, note: null }
            : m
        ));
        break;

      // ---- Reaction Updates ----
      case 'reaction_update':
        setMessages(prev => prev.map(m =>
          m.message_id === data.message_id
            ? { ...m, reactions: data.reactions }
            : m
        ));
        break;
      
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (user && token) {
      const socket = connectWS();
      return () => {
        if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        if (socket) socket.close();
      };
    }
  }, [user, token, connectWS]);

  // Send WS event
  const sendWSEvent = useCallback((event) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(event));
    }
  }, []);

  // Join a room
  const joinRoom = useCallback((roomId) => {
    sendWSEvent({ type: 'join_room', room_id: roomId });
  }, [sendWSEvent]);

  // Leave a room
  const leaveRoom = useCallback((roomId) => {
    sendWSEvent({ type: 'leave_room', room_id: roomId });
  }, [sendWSEvent]);

  // Send typing indicator
  const sendTyping = useCallback((roomId, isTyping) => {
    sendWSEvent({ type: 'typing', room_id: roomId, is_typing: isTyping });
  }, [sendWSEvent]);

  // Send read receipt
  const sendReadReceipt = useCallback((roomId, messageId) => {
    sendWSEvent({ type: 'receipt_read', room_id: roomId, message_id: messageId });
  }, [sendWSEvent]);

  // Load teams
  const loadTeams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/teams`);
      setTeams(res.data.teams);
      if (res.data.teams.length > 0 && !activeTeam) {
        setActiveTeam(res.data.teams[0]);
      }
    } catch (e) {
      console.error('Load teams error:', e);
    }
  }, [activeTeam]);

  // Load channels for a team
  const loadChannels = useCallback(async (teamId) => {
    try {
      const res = await axios.get(`${API}/channels?team_id=${teamId}`);
      setChannels(res.data.channels);
      if (res.data.channels.length > 0 && !activeChannel) {
        setActiveChannel(res.data.channels[0]);
      }
    } catch (e) {
      console.error('Load channels error:', e);
    }
  }, [activeChannel]);

  // Load DMs
  const loadDMs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dm`);
      setDms(res.data.dms);
    } catch (e) {
      console.error('Load DMs error:', e);
    }
  }, []);

  // Load messages
  const loadMessages = useCallback(async (channelId, parentId = null) => {
    try {
      let url = `${API}/messages?channel_id=${channelId}`;
      if (parentId) url += `&parent_id=${parentId}`;
      const res = await axios.get(url);
      if (!parentId) setMessages(res.data.messages);
      return res.data.messages;
    } catch (e) {
      console.error('Load messages error:', e);
      return [];
    }
  }, []);

  // Send message via REST (with optimistic update)
  const sendMessage = useCallback(async (channelId, content, parentId = null, messageType = 'text', attachments = [], codeSnippet = null, mentions = [], note = null) => {
    try {
      const res = await axios.post(`${API}/messages`, {
        channel_id: channelId,
        content,
        parent_id: parentId,
        message_type: messageType,
        attachments,
        code_snippet: codeSnippet,
        mentions,
        note
      });
      const msg = res.data.message;
      setMessages(prev => {
        if (prev.some(m => m.message_id === msg.message_id)) return prev;
        return [...prev, msg];
      });
      return msg;
    } catch (e) {
      console.error('Send message error:', e);
      throw e;
    }
  }, []);

  // Upload file
  const uploadFile = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.file;
  }, []);

  // Search messages
  const searchMessages = useCallback(async (query, channelId = null) => {
    try {
      let url = `${API}/messages/search?q=${encodeURIComponent(query)}`;
      if (channelId) url += `&channel_id=${channelId}`;
      const res = await axios.get(url);
      return res.data.messages;
    } catch (e) {
      console.error('Search error:', e);
      return [];
    }
  }, []);

  // Clear unread for channel
  const clearUnread = useCallback((channelId) => {
    setUnreadCounts(prev => ({ ...prev, [channelId]: 0 }));
  }, []);

  // Clear incoming call (after it's been handled by UI)
  const clearIncomingCall = useCallback(() => {
    setIncomingCall(null);
  }, []);

  // Clear call signal (after it's been consumed)
  const clearCallSignal = useCallback(() => {
    setCallSignal(null);
  }, []);

  // Consume signal from queue
  const consumeCallSignal = useCallback((signal) => {
    setCallSignals(prev => prev.filter(s => s !== signal));
  }, []);

  // Clear all signals
  const clearAllCallSignals = useCallback(() => {
    setCallSignals([]);
    setCallSignal(null);
  }, []);

  return (
    <ChatContext.Provider value={{
      connected, teams, channels, dms, activeChannel, activeTeam,
      messages, onlineUsers, typingUsers, unreadCounts,
      setActiveChannel, setActiveTeam, setChannels, setMessages,
      joinRoom, leaveRoom, sendTyping, sendReadReceipt,
      loadTeams, loadChannels, loadDMs, loadMessages,
      sendMessage, uploadFile, searchMessages, clearUnread,
      sendWSEvent,
      // Call signaling
      incomingCall, clearIncomingCall,
      callSignal, clearCallSignal,
      callSignals, consumeCallSignal, clearAllCallSignals
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be inside ChatProvider');
  return ctx;
};
